import React, {
  useState,
  useImperativeHandle,
  useRef,
  forwardRef,
} from "react";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Modal } from "ui-kit-ck-consultant";

const Camera = forwardRef(
  (
    {
      firstVideoRef,
      firstVideoWidth,
      firstVideoHeight,
      firstVideoIsMuted,
      secondVideoRef,
      secondVideoWidth,
      secondVideoHeight,
      secondVideoIsMuted,
      isTakePicture,
      countLoadPicture,
      pictures,
      onDeletePicture,
      onMarker,
      isMarker,
      statusConnection,
      children,
      connectionQuality,
      zoomLevel,
      isRecording,
      onStopRecording,
    },
    ref
  ) => {
    const [isFullModal, setIsFullModal] = useState(false);
    const [currentImage, setCurrentImage] = useState("");
    const canvasRef = useRef();

    function computeConnectionQuality({
      bitrateKbps,
      rtt,
      jitter,
      packetLoss,
    }) {
      // Convertir les valeurs en nombres
      const bitrate = parseFloat(bitrateKbps) || 0;
      const rttMs = parseFloat(rtt) || 0;
      const jitterMs = parseFloat(jitter) || 0;
      const loss = parseInt(packetLoss) || 0;

      let score = 5; // commence au max

      // Débit : <700 = mauvais, >1500 = excellent
      if (bitrate < 400) score -= 3;
      else if (bitrate < 800) score -= 2;
      else if (bitrate < 1500) score -= 1;

      // RTT : latence aller-retour
      if (rttMs > 300) score -= 3;
      else if (rttMs > 150) score -= 2;
      else if (rttMs > 80) score -= 1;

      // Jitter : fluctuation audio
      if (jitterMs > 80) score -= 3;
      else if (jitterMs > 50) score -= 2;
      else if (jitterMs > 30) score -= 1;

      // Pertes de paquets
      if (loss > 10) score -= 3;
      else if (loss > 5) score -= 2;
      else if (loss > 1) score -= 1;

      // Clamp entre 1 et 5
      return Math.min(5, Math.max(1, score));
    }

    // Composant de jauge réseau
    const NetworkQualityIndicator = ({ connectionQuality, pictures }) => {
      if (!connectionQuality) return null;

      const bars = [1, 2, 3, 4, 5];
      const qualityScore = computeConnectionQuality(connectionQuality);

      const getColor = () => {
        if (qualityScore >= 5) return "#4ade80"; // Vert
        if (qualityScore >= 4) return "#fbbf24"; // Jaune
        if (qualityScore >= 3) return "#fb923c"; // Orange
        return "#ef4444"; // Rouge
      };

      return (
        <div
          style={{
            position: "fixed",
            right: pictures && pictures.length > 0 ? "200px" : "30px",
            top: "30px",
            display: "flex",
            alignItems: "flex-end",
            gap: "3px",
            zIndex: 220,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            padding: "8px 12px",
            borderRadius: "8px",
          }}
        >
          {bars.map((bar) => (
            <div
              key={bar}
              style={{
                width: "6px",
                height: `${bar * 5}px`,
                backgroundColor:
                  bar <= qualityScore ? getColor() : "rgba(255, 255, 255, 0.3)",
                borderRadius: "2px",
                transition: "all 0.3s ease",
              }}
            />
          ))}
          <span
            style={{
              marginLeft: "8px",
              color: "#ffffff",
              fontSize: "12px",
              fontWeight: "500",
            }}
          >
            {qualityScore === 5 && "Excellent"}
            {qualityScore === 4 && "Bon"}
            {qualityScore === 3 && "Moyen"}
            {qualityScore === 2 && "Faible"}
            {qualityScore === 1 && "Très faible"}
          </span>
        </div>
      );
    };

    useImperativeHandle(ref, () => ({
      addMarker(x, y, width = null, height = null) {
        if (width && height) {
          x = (x * firstVideoRef.current.clientWidth) / width;
          y = (y * firstVideoRef.current.clientHeight) / height;
        }

        const canvas = canvasRef.current;
        const context = canvas.getContext("2d");
        context.beginPath();
        canvasArrow(context, x - 20, y - 20, x, y);
        context.stroke();
        getCanvasPNG();
      },

      clearCanvas() {
        const canvas = canvasRef.current;
        const context = canvas.getContext("2d");
        context.clearRect(0, 0, canvas.width, canvas.height);
      },

      getCanvas() {
        return canvasRef.current;
      },
    }));

    const canvasArrow = (context, fromx, fromy, tox, toy) => {
      let headlen = 15;
      context.strokeStyle = "red";
      context.lineWidth = 3;
      let dx = tox - fromx;
      let dy = toy - fromy;
      let angle = Math.atan2(dy, dx);
      context.moveTo(fromx, fromy);
      context.lineTo(tox, toy);
      context.lineTo(
        tox - headlen * Math.cos(angle - Math.PI / 5),
        toy - headlen * Math.sin(angle - Math.PI / 5)
      );
      context.moveTo(tox, toy);
      context.lineTo(
        tox - headlen * Math.cos(angle + Math.PI / 5),
        toy - headlen * Math.sin(angle + Math.PI / 5)
      );
    };

    const getCanvasPNG = () => {
      return canvasRef.current;
    };

    const width = firstVideoRef.current
      ? firstVideoRef.current.clientWidth
      : null;
    const height = firstVideoRef.current
      ? firstVideoRef.current.clientHeight
      : null;

    // IMPORTANT: Gérer correctement le son pour éviter l'écho
    // La vidéo locale doit toujours être muted (utiliser le prop firstVideoIsMuted/secondVideoIsMuted)
    // Seule la vidéo distante doit avoir du son

    if (firstVideoRef?.current) {
      if (firstVideoIsMuted) {
        // Vidéo locale - toujours muted
        firstVideoRef.current.volume = 0;
        firstVideoRef.current.muted = true;
      } else {
        // Vidéo distante - avec son
        firstVideoRef.current.volume = 1.0;
        firstVideoRef.current.muted = false;
      }
    }

    if (secondVideoRef?.current) {
      if (secondVideoIsMuted) {
        // Vidéo locale - toujours muted
        secondVideoRef.current.volume = 0;
        secondVideoRef.current.muted = true;
      } else {
        // Vidéo distante - avec son
        secondVideoRef.current.volume = 1.0;
        secondVideoRef.current.muted = false;
      }
    }

    return (
      <div
        style={{
          display: "flex",
          position: "fixed",
          top: 0,
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: "rgb(0, 0, 0)",
          zIndex: 200,
        }}
      >
        {/* Indicateur de qualité réseau */}
        <NetworkQualityIndicator
          connectionQuality={connectionQuality || {}}
          pictures={pictures}
        />

        {isRecording ? (
          <div
            style={{
              position: "fixed",
              top: "30px",
              left: "50%",
              transform: "translateX(-50%)",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              zIndex: 220,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              padding: "8px 14px",
              borderRadius: "8px",
              color: "#ffffff",
              fontSize: "13px",
              fontWeight: "500",
            }}
          >
            <div className="record-dot" />
            Enregistrement en cours
            {onStopRecording ? (
              <span
                style={{
                  marginLeft: "6px",
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
                onClick={onStopRecording}
              >
                Arrêter
              </span>
            ) : null}
          </div>
        ) : null}

        {countLoadPicture ? (
          <div
            style={{
              position: "fixed",
              left: "30px",
              bottom: "30px",
              zIndex: 1000,
            }}
          >
            <div
              className="bg-red"
              style={{ height: "15px", width: "15px", borderRadius: "15px" }}
            ></div>
          </div>
        ) : null}
        <div
          style={{
            position: "absolute",
            left: "2rem",
            top: "2rem",
            height: "120px",
            width: "120px",
            borderRadius: "60px",
            border: "2px rgba(255, 255, 255, 0.4) solid",
            backgroundColor: "rgb(0, 0, 0)",
            zIndex: 10,
            overflow: "hidden",
          }}
        >
          <video
            volume={0.95}
            style={{
              position: "absolute",
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%)",
              maxWidth:
                secondVideoWidth < secondVideoHeight ? "100%" : "inherit",
              maxHeight:
                secondVideoHeight < secondVideoWidth ? "100%" : "inherit",
            }}
            src={null}
            autoPlay
            playsInline
            muted={false}
            ref={secondVideoRef}
          />
        </div>
        <div
          style={{
            position: "absolute",
            display: "flex",
            left: "0",
            right: "0",
            top: "0",
            bottom: "0",
            overflow: "hidden",
          }}
        >
          <video
            volume={0.95}
            className={isTakePicture ? "camera-animation" : ""}
            style={{
              margin: "auto",
              width: firstVideoWidth > firstVideoHeight ? "100%" : "inherit",
              height: firstVideoHeight > firstVideoWidth ? "100%" : "inherit",
            }}
            src={null}
            autoPlay
            playsInline
            muted={firstVideoIsMuted}
            ref={firstVideoRef}
          />
          <canvas
            ref={canvasRef}
            width={width}
            height={height}
            style={{
              position: "absolute",
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%)",
              width: width || "inherit",
              height: height || "inherit",
            }}
            onClick={(e) => {
              if (isMarker && ref?.current) {
                let currentTargetRect = e.currentTarget.getBoundingClientRect();
                const x = e.clientX - currentTargetRect.left;
                const y = e.clientY - currentTargetRect.top;
                ref.current.addMarker(x, y, width, height);
                onMarker(x, y, width, height);
              }
            }}
          />
        </div>
        {zoomLevel && zoomLevel > 1 && (
          <div
            style={{
              position: "fixed",
              left: "30px",
              bottom: "90px",
              zIndex: 220,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              padding: "4px 12px",
              borderRadius: "8px",
              color: "#ffffff",
              fontSize: "14px",
              fontWeight: "500",
            }}
          >
            {zoomLevel.toFixed(1)}x
          </div>
        )}
        <div
          style={{
            margin: "auto",
            marginBottom: "1rem",
            color: "#ffffff",
            zIndex: 20,
          }}
        >
          {children}
        </div>
        {pictures && pictures.length > 0 && (
          <div
            style={{
              position: "fixed",
              top: 0,
              right: 0,
              bottom: 0,
              width: "180px",
              backgroundColor: "rgba(255, 255, 255, 0.4)",
              zIndex: 210,
              padding: "1rem",
              overflowY: "auto",
            }}
          >
            {[...pictures].reverse().map((element, idx) => (
              <div
                key={element.id}
                style={{
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    position: "absolute",
                    right: 0,
                    top: 0,
                    width: "40px",
                    height: "40px",
                    backgroundColor: "rgba(255, 255, 255, 0.6)",
                    cursor: "pointer",
                    color: "red",
                    borderBottomLeftRadius: "10px",
                    borderTopRightRadius: "10px",
                  }}
                  onClick={() => onDeletePicture(element.id)}
                >
                  <FontAwesomeIcon style={{ margin: "auto" }} icon={faTrash} />
                </div>
                <img
                  src={element.base64}
                  alt={idx}
                  style={{
                    maxWidth: "100%",
                    marginBottom: "1rem",
                    borderRadius: "10px",
                  }}
                  onClick={() => {
                    setIsFullModal(true);
                    setCurrentImage(element.base64);
                  }}
                />
              </div>
            ))}
          </div>
        )}
        {statusConnection === 0 && (
          <div
            style={{
              position: "fixed",
              left: 0,
              right: 0,
              top: "50%",
              transform: "translateY(-50%)",
              textAlign: "center",
              backgroundColor: "rgba(255, 255, 255, 0.6)",
            }}
          >
            <p>Recherche du partenaire...</p>
          </div>
        )}
        {statusConnection === 1 && (
          <div
            style={{
              position: "fixed",
              left: 0,
              right: 0,
              top: "50%",
              transform: "translateY(-50%)",
              textAlign: "center",
              backgroundColor: "rgba(255, 255, 255, 0.6)",
            }}
          >
            <p>Connexion au partenaire...</p>
          </div>
        )}
        <Modal show={isFullModal} onClose={() => setIsFullModal(false)}>
          <img
            src={currentImage}
            alt="full"
            style={{ maxWidth: "100%", maxHeight: "100%" }}
          />
        </Modal>
      </div>
    );
  }
);

export default Camera;
